import { acss } from '@adornis/chemistry/directives/acss';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design';
import { askForFile } from '@adornis/file-utils/client/helper-methods';
import { stateful, useState } from '@adornis/functional-lit/stateful';
import { html } from 'lit';
import { SupportedFileType } from './las-file-explorer';

export const FileUploadArea = stateful(
  ({
    allowedFileTypes = [],
    onValuePicked,
    title = 'Klicken oder Dateien hierher ziehen, um hochzuladen.',
    acceptMultipleFiles = true,
  }: {
    onValuePicked: (files: FileList | File[]) => Promise<void>;
    title?: string;
    allowedFileTypes?: SupportedFileType[];
    acceptMultipleFiles?: boolean;
  }) => {
    const [isDragging, setIsDragging] = useState(false);
    const design = DesignSystem.currentTheme.getValue();

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer?.files;
      if (!droppedFiles || droppedFiles.length === 0) return;

      let filesToUpload = Array.from(droppedFiles);

      if (allowedFileTypes.length > 0) {
        filesToUpload = filesToUpload.filter(file => {
          const extension = file.name.split('.').pop()?.toLowerCase() || '';
          return allowedFileTypes.includes(extension as SupportedFileType);
        });

        if (filesToUpload.length === 0) {
          XSnackbar.show('Keine gültigen Dateien zum Hochladen gefunden.');
          return;
        }

        if (filesToUpload.length < droppedFiles.length) {
          XSnackbar.show(
            `Nur ${filesToUpload.length} von ${droppedFiles.length} Dateien haben ein unterstütztes Format.`,
          );
        }

        onValuePicked(filesToUpload);
      } else {
        onValuePicked(droppedFiles);
      }
    };

    const onClick = async () => {
      const files = await askForFile({
        acceptFileTypes: allowedFileTypes.length > 0 ? `${allowedFileTypes.map(type => `.${type}`).join(',')}` : '',
        acceptMultipleFiles,
      });

      if (!files) return;
      onValuePicked(files);
    };

    return html`
      <d-flex
        ${acss({
          border: `1px dashed ${isDragging ? design.colors.tone.black : design.colors.tone.secondaryText}`,
          padding: design.spacing.lg,
          borderRadius: design.sizes.borderRadius,
          backgroundColor: isDragging ? design.colors.tone.secondaryBackground : 'transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderStyle: 'solid',
            cursor: 'pointer',
            borderColor: design.colors.tone.black,
            'd-text': {
              color: design.colors.tone.black,
            },
          },
        })}
        center
        crossaxis-center
        @click=${onClick}
        @dragover=${onDragOver}
        @dragleave=${onDragLeave}
        @drop=${onDrop}
      >
        <d-text grey bold>
          <d-icon solid> ${isDragging ? 'cloud-arrow-up' : 'upload'} </d-icon>
          ${isDragging ? 'Ablegen...' : title}
        </d-text>
      </d-flex>
    `;
  },
);
